import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { tenantService, Tenant } from '../services/tenantService';
import { useAuth } from './AuthContext';

interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  setCurrentTenant: (tenant: Tenant | null) => void;
  loading: boolean;
  error: string | null;
  refreshTenant: () => void;
  refreshKey: number;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadTenants = useCallback(async () => {
    if (!user) {
      setCurrentTenant(null);
      setAvailableTenants([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (user.role === 'admin') {
        // Admin users can see all tenants
        const tenants = await tenantService.getTenants();
        setAvailableTenants(tenants.filter(t => t.is_active));

        // Set default tenant (first active tenant or previously selected)
        const savedTenantId = localStorage.getItem('selectedTenantId');
        let defaultTenant = tenants.find(t => t.is_active && t.id.toString() === savedTenantId);

        if (!defaultTenant && tenants.length > 0) {
          defaultTenant = tenants.find(t => t.is_active) || tenants[0];
        }

        setCurrentTenant(defaultTenant || null);
      } else if (user.tenant_id) {
        // Therapist users only see their assigned tenant
        try {
          const tenant = await tenantService.getMyTenant();
          setCurrentTenant(tenant);
          setAvailableTenants([tenant]);
        } catch (err) {
          console.error('Error loading user tenant:', err);
          setCurrentTenant(null);
          setAvailableTenants([]);
        }
      } else {
        // User has no tenant assigned
        setCurrentTenant(null);
        setAvailableTenants([]);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
      setCurrentTenant(null);
      setAvailableTenants([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTenants();
  }, [user, refreshKey, loadTenants]);

  const handleSetCurrentTenant = (tenant: Tenant | null) => {
    setCurrentTenant(tenant);
    if (tenant && user?.role === 'admin') {
      localStorage.setItem('selectedTenantId', tenant.id.toString());
    } else {
      localStorage.removeItem('selectedTenantId');
    }
    // Trigger a refresh for all pages
    setRefreshKey(prev => prev + 1);
  };

  const refreshTenant = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        availableTenants,
        setCurrentTenant: handleSetCurrentTenant,
        loading,
        error,
        refreshTenant,
        refreshKey,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};